import { Link } from 'react-router-dom';
import { Shield, Eye, Lock, FileText, Users, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  const sections = [
    {
      icon: FileText,
      title: 'Information We Collect',
      content: [
        'Personal identification information (name, email address, phone number)',
        'Medical history and health-related information',
        'Appointment and service records',
        'Insurance and payment information',
        'Website usage data and cookies'
      ]
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: [
        'To provide medical services and patient care',
        'To schedule and manage appointments',
        'To communicate with you about your health and our services',
        'To process payments and insurance claims',
        'To improve our services and patient experience',
        'To comply with legal and regulatory requirements'
      ]
    },
    {
      icon: Shield,
      title: 'How We Protect Your Information',
      content: [
        'We use industry-standard encryption for data transmission',
        'Access to patient information is restricted to authorized personnel',
        'Regular security audits and updates to our systems',
        'Physical security measures at our facilities',
        'Staff training on data protection and privacy'
      ]
    },
    {
      icon: Users,
      title: 'Information Sharing',
      content: [
        'We do not sell your personal information to third parties',
        'Information may be shared with healthcare providers involved in your care',
        'We may share information with insurance providers for billing purposes',
        'Information may be disclosed when required by law',
        'Anonymized data may be used for research and quality improvement'
      ]
    },
    {
      icon: Lock,
      title: 'Your Rights',
      content: [
        'Access your personal and medical information',
        'Request corrections to inaccurate information',
        'Request deletion of your information (subject to legal requirements)',
        'Opt out of marketing communications',
        'File a complaint if you believe your privacy rights have been violated'
      ]
    },
    {
      icon: AlertCircle,
      title: 'Contact Us About Privacy',
      content: [
        'If you have questions about this privacy policy or our data practices, please contact our Privacy Officer:',
        'Email: privacy@baobabhospital.co.tz',
        'Phone: +255 123 456 789',
        'Address: Mapinga Road, Bagamoyo, Pwani Region, Tanzania'
      ]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-16 md:py-24" data-testid="privacy-hero">
        <div className="container-custom">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6" data-testid="privacy-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="max-w-3xl">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              At BAOBAB Hospital, we are committed to protecting your privacy and ensuring the security of your personal and medical information. This policy outlines how we collect, use, and protect your data.
            </p>
            <p className="text-white/60 text-sm mt-4">
              Last updated: January 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing" data-testid="privacy-content">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {sections.map((section, index) => (
              <div key={index} className="mb-12" data-testid={`privacy-section-${index}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-primary">
                    {section.title}
                  </h2>
                </div>
                <div className="pl-16">
                  <ul className="space-y-3">
                    {section.content.map((item, i) => (
                      <li key={i} className="text-slate-600 leading-relaxed flex items-start">
                        <span className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}

            {/* Additional Info */}
            <div className="bg-warm rounded-2xl p-8 mt-12">
              <h3 className="font-heading font-semibold text-primary text-xl mb-4">
                Changes to This Policy
              </h3>
              <p className="text-slate-600 mb-4">
                We may update this privacy policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically.
              </p>
              <p className="text-slate-600">
                If we make significant changes, we will notify you through our website or by other appropriate means.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary" data-testid="privacy-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            Have Questions About Our Privacy Practices?
          </h2>
          <p className="text-white/80 mb-8">
            We're here to help. Contact our Privacy Officer for more information.
          </p>
          <Link to="/contact" data-testid="privacy-contact">
            <Button className="bg-accent hover:bg-coral-soft text-white rounded-full px-8 py-4">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
